import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Anchor, Ship, MapPin, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { SYDNEY_COUNCILS_MAP, MAP_STAGES, MAP_BOUNDS, RIVER_GEO } from "@/data";

const { lngMin, lngMax, latMin, latMax } = MAP_BOUNDS;
const pt = ([lng, lat]) => [
    ((lng - lngMin) / (lngMax - lngMin)) * 1000,
    ((latMax - lat) / (latMax - latMin)) * 480,
];

const DOT_COLORS = ["#34D399", "#F2E7C9", "#D97B4F", "#7DD3FC"];
const DOTS = RIVER_GEO.flatMap((ll, i) => {
    const [x, y] = pt(ll);
    return [
        { x: x + Math.sin(i * 2.3) * 7, y: y - 22 - (i % 3) * 5, c: DOT_COLORS[i % 4] },
        { x: x + Math.cos(i * 1.9) * 7, y: y + 22 + (i % 2) * 5, c: DOT_COLORS[(i + 2) % 4] },
    ];
});

export default function PodMap() {
    const [active, setActive] = useState(SYDNEY_COUNCILS_MAP[0]);
    const [view, setView] = useState({ s: 1, x: 0, y: 0 });
    const dragRef = useRef(null);
    const canvasRef = useRef(null);

    const dims = () => {
        const el = canvasRef.current;
        return { W: el?.clientWidth || 1000, H: el?.clientHeight || 480 };
    };

    const clampTo = (v) => {
        const { W, H } = dims();
        return {
            s: v.s,
            x: Math.min(0, Math.max(W - v.s * W, v.x)),
            y: Math.min(0, Math.max(H - v.s * H, v.y)),
        };
    };

    const zoom = (factor) => {
        const { W, H } = dims();
        setView((v) => {
            const s = Math.min(2.8, Math.max(1, v.s * factor));
            if (s === 1) return { s: 1, x: 0, y: 0 };
            const nx = W / 2 - ((W / 2 - v.x) * s) / v.s;
            const ny = H / 2 - ((H / 2 - v.y) * s) / v.s;
            return clampTo({ s, x: nx, y: ny });
        });
    };

    const focusOn = (council) => {
        const { W, H } = dims();
        const [sx, sy] = pt(council.spots[0].ll);
        const px = (sx / 1000) * W;
        const py = (sy / 480) * H;
        const s = 1.9;
        setView(clampTo({ s, x: W / 2 - s * px, y: H / 2 - s * py }));
    };

    const onPointerDown = (e) => {
        dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: view.x, baseY: view.y, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        if (Math.abs(dx) + Math.abs(dy) > 6) d.moved = true;
        if (d.moved) setView((v) => clampTo({ ...v, x: d.baseX + dx, y: d.baseY + dy }));
    };
    const onPointerUp = () => {
        setTimeout(() => {
            if (dragRef.current) dragRef.current = null;
        }, 0);
    };
    const select = (council) => {
        if (dragRef.current?.moved) return;
        setActive(council);
    };
    const selectFromChip = (council) => {
        setActive(council);
        focusOn(council);
    };

    return (
        <div className="mt-20" data-testid="pod-map-section">
            <p className="text-sm font-semibold tracking-[0.3em] uppercase text-sky-200 mb-4" data-testid="pod-map-eyebrow">
                Deployment
            </p>
            <h3 className="font-display font-semibold text-3xl md:text-4xl" data-testid="pod-map-heading">
                The Year 6 map: thirteen councils, 117 Guardians
            </h3>
            <p className="mt-4 text-lg text-slate-400 max-w-3xl leading-relaxed">
                A projection of how far the fleet reaches by the sixth year of operations, plotted on satellite
                imagery of the real harbour. Every council fields nine vessels across its named spots. Pick a
                council below to fly to it, or zoom and drag the map yourself.
            </p>

            <div className="mt-8 flex flex-wrap gap-2" data-testid="map-council-chips">
                {SYDNEY_COUNCILS_MAP.map((council) => (
                    <button
                        key={council.id}
                        data-testid={`map-chip-${council.id}`}
                        onClick={() => selectFromChip(council)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                            active.id === council.id
                                ? council.highlight
                                    ? "bg-emerald-400 text-[#020D18] border-emerald-400"
                                    : "bg-sky-400 text-[#020D18] border-sky-400"
                                : "border-[#1C3A54] text-slate-300 hover:border-sky-300 hover:text-sky-200"
                        }`}
                    >
                        {council.name}
                    </button>
                ))}
            </div>

            <div className="relative mt-4 rounded-xl overflow-hidden border border-[#12283C]">
                <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 md:p-6 bg-[#020D18]">
                    <div
                        ref={canvasRef}
                        className="lg:col-span-2 relative rounded-xl overflow-hidden border border-[#12283C] cursor-grab active:cursor-grabbing touch-none select-none"
                        data-testid="pod-map-canvas"
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerLeave={onPointerUp}
                    >
                        <div
                            className="will-change-transform"
                            style={{
                                transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`,
                                transformOrigin: "0 0",
                                transition: dragRef.current ? "none" : "transform 0.35s ease-out",
                            }}
                        >
                            <img
                                src="/images/sydney-satellite.jpg"
                                alt="Satellite view of Sydney Harbour and the Parramatta River"
                                className="w-full block brightness-[0.68] saturate-[0.85] pointer-events-none"
                                draggable={false}
                            />
                            <svg
                                viewBox="0 0 1000 480"
                                preserveAspectRatio="none"
                                className="absolute inset-0 w-full h-full"
                                role="img"
                                aria-label="Projected RiverGuardian stations by council, Year 6"
                            >
                                {DOTS.map((dot, i) => (
                                    <circle key={i} cx={dot.x} cy={dot.y} r="3" fill={dot.c} opacity="0.85" />
                                ))}

                                {SYDNEY_COUNCILS_MAP.map((council) => {
                                    const [lx, ly] = pt(council.ll);
                                    return (
                                        <g key={council.id}>
                                            <text
                                                x={lx}
                                                y={ly}
                                                onClick={() => select(council)}
                                                className="cursor-pointer"
                                                fill={council.highlight ? "#A7F3D0" : active.id === council.id ? "#E2F5FF" : "#BAE6FD"}
                                                fontSize="15.5"
                                                fontFamily="Instrument Sans, sans-serif"
                                                fontWeight={council.highlight || active.id === council.id ? 700 : 600}
                                                style={{ paintOrder: "stroke", stroke: "#020D18", strokeWidth: 4, strokeLinejoin: "round" }}
                                                data-testid={`council-label-${council.id}`}
                                            >
                                                {council.name}
                                            </text>
                                            {council.spots.map((spot) => {
                                                const [sx, sy] = pt(spot.ll);
                                                const isActive = active.id === council.id;
                                                return (
                                                    <g
                                                        key={spot.name}
                                                        onClick={() => select(council)}
                                                        onMouseEnter={() => {
                                                            if (!dragRef.current) setActive(council);
                                                        }}
                                                        className="cursor-pointer"
                                                        data-testid={`spot-${spot.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                    >
                                                        {council.highlight && (
                                                            <circle cx={sx} cy={sy} r="14" fill="#34D399" opacity="0.2" />
                                                        )}
                                                        <circle
                                                            cx={sx}
                                                            cy={sy}
                                                            r={isActive ? 6.5 : 4.5}
                                                            fill={council.highlight ? "#34D399" : isActive ? "#7DD3FC" : "#38BDF8"}
                                                            stroke={isActive || council.highlight ? "#E0F2FE" : "#020D18"}
                                                            strokeWidth={isActive || council.highlight ? 2 : 1}
                                                            opacity={isActive ? 1 : 0.85}
                                                        />
                                                        {isActive && (
                                                            <text
                                                                x={sx}
                                                                y={sy + 18}
                                                                textAnchor="middle"
                                                                fill="#E0F2FE"
                                                                fontSize="11.5"
                                                                fontFamily="Instrument Sans, sans-serif"
                                                                style={{ paintOrder: "stroke", stroke: "#020D18", strokeWidth: 3.5, strokeLinejoin: "round" }}
                                                            >
                                                                {spot.name}
                                                            </text>
                                                        )}
                                                    </g>
                                                );
                                            })}
                                        </g>
                                    );
                                })}
                            </svg>
                        </div>

                        <div className="absolute bottom-4 left-4 flex gap-2" data-testid="map-zoom-controls">
                            <button
                                data-testid="map-zoom-in"
                                onClick={() => zoom(1.35)}
                                className="w-9 h-9 rounded-lg bg-[#020D18]/85 border border-[#1C3A54] text-sky-200 flex items-center justify-center hover:border-sky-300 transition-colors"
                                aria-label="Zoom in"
                            >
                                <ZoomIn className="w-4 h-4" />
                            </button>
                            <button
                                data-testid="map-zoom-out"
                                onClick={() => zoom(1 / 1.35)}
                                className="w-9 h-9 rounded-lg bg-[#020D18]/85 border border-[#1C3A54] text-sky-200 flex items-center justify-center hover:border-sky-300 transition-colors"
                                aria-label="Zoom out"
                            >
                                <ZoomOut className="w-4 h-4" />
                            </button>
                            <button
                                data-testid="map-zoom-reset"
                                onClick={() => setView({ s: 1, x: 0, y: 0 })}
                                className="w-9 h-9 rounded-lg bg-[#020D18]/85 border border-[#1C3A54] text-sky-200 flex items-center justify-center hover:border-sky-300 transition-colors"
                                aria-label="Reset map view"
                            >
                                <Maximize2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <motion.div
                        key={active.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`rounded-xl p-7 border ${
                            active.highlight
                                ? "bg-[#05251C] border-emerald-400/50"
                                : "bg-[#05251C] border-emerald-900/60"
                        }`}
                        data-testid="pod-detail-card"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-10 h-10 rounded-lg bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center">
                                <Anchor className="w-5 h-5 text-emerald-300" />
                            </span>
                            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-emerald-300/80">Station brief</p>
                        </div>
                        <p className="font-display font-semibold text-2xl" data-testid="pod-detail-name">{active.name}</p>
                        <div className="mt-4 flex flex-col gap-3 text-base">
                            <p className="flex items-center gap-2 text-sky-200 font-semibold">
                                <Ship className="w-4 h-4 shrink-0" />
                                <span data-testid="pod-detail-vessels">9 vessels</span>
                            </p>
                            <p className="text-emerald-300/90 text-sm font-semibold" data-testid="pod-detail-stage">
                                {MAP_STAGES[active.stage - 1].year} stage
                            </p>
                            <div data-testid="pod-detail-spots">
                                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500 mb-2">Deployment spots</p>
                                {active.spots.map((spot, i) => (
                                    <p key={spot.name} className="flex items-center gap-2 text-slate-300 text-sm py-1" data-testid={`pod-spot-${i}`}>
                                        <MapPin className="w-3.5 h-3.5 text-sky-200 shrink-0" />
                                        {spot.name}
                                    </p>
                                ))}
                            </div>
                            <p className="text-slate-400 leading-relaxed" data-testid="pod-detail-note">{active.note}</p>
                        </div>
                    </motion.div>
                </div>
                <p className="px-4 md:px-6 pb-4 text-sm text-slate-500 bg-[#020D18]" data-testid="pod-map-caption">
                    Satellite basemap: Esri World Imagery. Projection of fleet reach by Year 6 of operations,
                    each council fields nine vessels across its named spots. Indicative siting only; dot-work
                    along the river honours the waterway's First Peoples.
                </p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2" data-testid="map-stages">
                {MAP_STAGES.map((s, i) => (
                    <span
                        key={s.year}
                        data-testid={`map-stage-${i}`}
                        className="text-sm px-4 py-2 rounded-full border border-[#1C3A54] text-slate-300 bg-[#06182A]"
                    >
                        <span className="text-sky-200 font-semibold">{s.year}</span> {s.label}
                    </span>
                ))}
            </div>

            <div className="relative mt-6 rounded-xl overflow-hidden border border-[#12283C]" data-testid="map-photo-band">
                <img
                    src="/images/harbour-peninsulas.webp"
                    alt="Aerial view of Sydney Harbour's peninsulas and bays"
                    className="w-full h-[280px] md:h-[340px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-[#020D18]/25 to-transparent" />
                <div className="absolute bottom-0 p-6">
                    <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">The operating area</p>
                    <p className="mt-2 text-base text-slate-300 max-w-2xl leading-relaxed">
                        Sydney Harbour and the Parramatta River system from above Hunters Hill, the full Year 6
                        operating area, photographed on location.
                    </p>
                </div>
            </div>
        </div>
    );
}
